/**
 * Store pour la gestion des statistiques des comptes des réseaux sociaux
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';
/***/
import { useManagingUser } from '@compositions/ManagingUser';

export const useSocialNetworkAccountStatisticStore = defineStore('socialNetworkAccountStatistic', (() => {

    // Composition pour l'utilisateur que l'on gère
    const { getManagingUser } = useManagingUser({});

    // State de la liste des comptes (total et items)
    const accountList = ref({});

    // State des comptes chargés
    const accounts = ref([]);

    /**
     * Retourne la liste complète des comptes chargés
     * @return array
     */
    const getAccounts = computed(() => accounts.value);

    /**
     * Retourne les données de la liste des comptes (total, accounts)
     * @return object
     */
    const getAccountList = computed(() => accountList.value);

    /**
     * Retourne le compte dont l'identifiant est en paramètre
     * @return object|null
     */
    const getAccount = computed(() => {
        return ((accountId) => {
            const accountFound = accounts.value.filter((currentAccount) => currentAccount.id === accountId).at(0);
            return (accountFound ?? null);
        });
    })

    /**
     * Chargement de la liste des comptes
     * @param object params Paramètres pour la requête Ajax
     * @return Promise
     */
    const loadAccountList = ((params) => {

        const userId = getManagingUser.value.id;

        const path = `/api/users/${ userId }/account/social/accounts/statistics`;

        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {

                if(params.offset === 0) {
                    resetAccountList();
                }

                updateAccountList(response.data.data);

                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Mise à jour de la liste des comptes avec les données en paramètre
     * @return void
     */
    const updateAccountList = ((data) => {

        if(accountList.value.total === undefined) {
            accountList.value = {
                total: data.total,
                accounts: data.items
            };

            data.items.forEach((account) => {
                updateAccount(account);
            });

        } else {
            accountList.value.total = data.total;

            const currentAccounts = accountList.value.accounts;
            const newAccounts = data.items.filter((item) => ! currentAccounts.find((current) => current.id === item.id));
            accountList.value.accounts = [ ...currentAccounts, ...newAccounts ];

            newAccounts.forEach((account) => {
                updateAccount(account);
            });
        }

    });

    /**
     * Mise à jour des données dans le store du compte en paramètre
     * @param object account
     * @return void
     */
    const updateAccount = ((account) => {

        const accountId = account.id;

        const index = accounts.value.findIndex((accountFromState) => accountFromState.id === accountId);
        if(index !== -1) {
            accounts.value[index] = account;
        } else {
            accounts.value.push(account);
        }

        // Synchronisation avec les éléments de la liste
        const listData = accountList.value;
        if(listData.total !== undefined) {
            const listAccounts = listData.accounts || [];
            const indexInList = listAccounts.findIndex((accountFromState) => accountFromState.id === accountId);
            if(indexInList !== -1) {
                listAccounts[indexInList] = account;
            } else {
                accountList.value.total += 1;
                listAccounts.push(account);
            }

            accountList.value.accounts = listAccounts;
        }
    });

    /**
     * Réinitialisation de la liste des comptes
     * @return void
     */
    const resetAccountList = (() => {
        accountList.value = {};
    });

    /**
     * Réinitialisation des données du store
     * @return void
     */
    const reset = (() => {
        resetAccountList();
        accounts.value = [];
    });

    return {
        getAccounts,
        getAccountList,
        loadAccountList,
        getAccount,
        reset
    };

}));
